import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ADK_API_BASE_URL } from './config/api';
import './App.css';
import ChatInterfaceNew from './components/ChatInterfaceNew';
import { OAuthProvider, useOAuth } from './auth/OAuthContext';

function App() {
  return (
    <OAuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Main route - handles OAuth callback and main interface */}
            <Route path="/" element={<ChatWrapper />} />

            {/* Legacy /chat route - redirect to root */}
            <Route path="/chat" element={<Navigate to="/" replace />} />

            {/* Legacy /login route - redirect to root */}
            <Route path="/login" element={<OAuthCallbackHandler />} />

            {/* Redirect any unknown routes to root */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </OAuthProvider>
  );
}

// OAuth callback handler component
function OAuthCallbackHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adoptCredentials } = useOAuth();

  React.useEffect(() => {
    const error = searchParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      alert(`Authentication failed: ${error}`);
      navigate('/', { replace: true });
      return;
    }

    // Tokens no longer arrive in the URL. The callback leaves them in a
    // short-lived HttpOnly cookie, which this exchanges for a JSON body once.
    // Putting them in the query string sent a live access token and a
    // non-expiring refresh token into the address bar, browser history, the
    // Referer header of every later request, and any proxy's logs.
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${ADK_API_BASE_URL}/oauth/handoff`, {
          method: 'POST',
          credentials: 'same-origin',
        });
        // Deliberately NOT bailing on `cancelled` here. The handoff cookie is
        // single-use and the server has already cleared it, so discarding the
        // response would strand the sign-in with nothing left to retry. Only
        // the navigation below is skipped when this effect is torn down.
        if (response.ok) {
          const creds = await response.json();
          if (creds?.access_token && creds?.email) {
            // Hand them to the provider, not just to sessionStorage. The
            // navigate() below is a client-side route change, so the provider
            // never remounts and its mount-time sessionStorage check never
            // runs again — which is why signing in once left the app showing
            // the login screen and everyone signed in twice.
            adoptCredentials(creds);
          }
        }
        // A 401 means there is no handoff in progress — someone opened /login
        // directly, or the cookie was already exchanged. Fall through to the
        // main page, which sends them back to sign in.
      } catch (err) {
        console.error('OAuth handoff failed:', err);
      } finally {
        if (!cancelled) navigate('/', { replace: true });
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, navigate, adoptCredentials]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8f9fa',
      fontSize: '18px'
    }}>
      Loading...
    </div>
  );
}

// Chat wrapper component
function ChatWrapper() {
  const location = useLocation();
  const [hasProcessedQuery, setHasProcessedQuery] = React.useState(false);

  const handleBackToLanding = () => {
    // For now, just stay on main page
  };

  // Get initial query from navigation state if available
  const state = location.state as { initialQuery?: string };
  const initialQuery = state?.initialQuery;

  return (
    <ChatInterfaceNew
      onBack={handleBackToLanding}
      initialQuery={!hasProcessedQuery ? initialQuery : undefined}
    />
  );
}

export default App;